
import { defineComponent, ref, reactive, onMounted, onBeforeUnmount, onBeforeUpdate, computed, watch } from 'vue'
import { useStore } from 'vuex'

import { useHead } from '@vueuse/head'

import { RepositoryFactory } from '@/repositories/RepositoryFactory'
const SeasonRepository = RepositoryFactory.get('season')
const CategoryRepository = RepositoryFactory.get('category')

import BaseBreadcrumb, { Breadcrumb } from '@/components/BaseBreadcrumb.vue'
import BaseDialog from '@/components/BaseDialog.vue'


import { Dialog } from '@/types/dialog'
import { Season } from '@/types/season'
import { TopicCategory } from '@/types/topicCategory'

export default defineComponent({
  name: 'SeasonHome',
  inheritAttrs: false,
  components: {
    BaseBreadcrumb,
    BaseDialog
  },
  setup() {
    const store = useStore()

    onMounted(() => {
      window.addEventListener('click', closeSeasonSubmenu)

      getTopicCategories()
      getSeasons('initial')

      seasons.infiniteScrollObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return

          if ((seasons.list.length < seasons.totalNumber) && !seasons.isLoading) getSeasons('scroll')
        })
      })

      if (seasonsObserverTarget.value !== undefined) {
        seasons.infiniteScrollObserver.observe(seasonsObserverTarget.value)
      }
    })

    onBeforeUnmount(() => {
      window.removeEventListener('click', closeSeasonSubmenu)
    })

    onBeforeUpdate(() => {
      seasonSubmenuElements.value = []
    })

    const seasons: {
      list: Season[]
      selected?: Season

      limit: number,
      page: number,
      totalNumber: number,

      isLoading: boolean,
      infiniteScrollObserver?: IntersectionObserver
    } = reactive({
      list: [],
      selected: undefined,

      limit: 20,
      page: 1,
      totalNumber: 0,

      isLoading: false,
      infiniteScrollObserver: undefined
    })

    const seasonsObserverTarget = ref<HTMLElement>()

    const getSeasons = async (type: string) => {
      try {
        seasons.isLoading = true

        if (type == 'initial' || type == 'search') seasons.page = 1

        const dataObj = {
          limit: seasons.limit,
          page: seasons.page,
          category_id: searchForm.category_id,
          keyword: searchForm.keyword,
          archived: searchForm.isArchived
        }

        const res = await SeasonRepository.getSeasons(dataObj)

        if (res.status === 200) {
          const r = res.data
          const data = r.data

          if (type == 'initial' || type == 'search') {
            seasons.list = data.seasons
          } else {
            seasons.list = seasons.list.concat(data.seasons)
          }

          seasons.totalNumber = data.total_number

          seasons.page += 1
        }
      } catch (error) {
        //
      } finally {
        seasons.isLoading = false
      }
    }

    const topicCategories: {
      list: TopicCategory[]
    } = reactive({
      list: []
    })

    const getTopicCategories = async () => {
      try {
        const res = await CategoryRepository.getTopicCategories()

        if (res.status === 200) {
          const r = res.data
          const data = r.data

          topicCategories.list = data.categories
        }
      } catch (error) {
        //
      }
    }

    const searchForm = reactive({
      isArchived: false,
      category_id: undefined,
      keyword: ''
    })

    watch(
      () => searchForm,
      () => {
        getSeasons('search')
      },
      { deep: true }
    )

    const isSearching = computed(() => {
      if (searchForm.isArchived === true || searchForm.category_id != undefined || searchForm.keyword !== '') {
        return true
      } else {
        return false
      }
    })

    const initializeSelectedSeason = () => {
      seasons.selected = undefined
    }

    const seasonSubmenuElements = ref()

    const openSeasonSubmenu = (season :Season) => {
      seasons.selected = season
    }

    const closeSeasonSubmenu = (event :Event) => {
      const elements = seasonSubmenuElements.value
      for (let i = 0; i < elements.length; i++) {
        const activeElement = elements[i] as HTMLElement
        const clickTarget = event.target as HTMLElement
        if (activeElement.contains(clickTarget)) {
          return
        }
      }

      initializeSelectedSeason()
    }

    const seasonDialogArchive = reactive<Dialog>({
      title: '{イベント名}{シーズン名} をアーカイブしますか？',
      message: 'アーカイブすると、トップページに表示されなくなります。\n詳細画面や、外部表示では閲覧が可能です。',
      confirmText: 'アーカイブする',
      cancelText: 'キャンセル',

      isOpen: false
    })

    const openSeasonDialogArchive = (season: Season) => {
      seasons.selected = season
      seasonDialogArchive.title = `${seasons.selected.topic_name} ${seasons.selected.name} をアーカイブしますか？`

      seasonDialogArchive.isOpen = true
    }

    const closeSeasonDialogArchive = () => {
      seasonDialogArchive.isOpen = false
      initializeSelectedSeason()
    }

    const archiveSeason = async () => {
      try {
        const dataObj = {
          season_id: seasons.selected?.id
        }

        const res = await SeasonRepository.archiveSeason(dataObj)

        if (res.status === 200) {
          seasons.list = []
          getSeasons('initial')
          closeSeasonDialogArchive()

          store.dispatch('addFlashMessage', {
            type: 'success',
            message: '大会をアーカイブしました'
          })
        }
      } catch (error) {
        //
      }
    }

    const seasonDialogDelete = reactive<Dialog>({
      title: '{イベント名}{シーズン名} を削除しますか？',
      message: '削除文言',
      confirmText: '削除する',
      cancelText: 'キャンセル',

      isOpen: false
    })

    const openSeasonDialogDelete = (season: Season) => {
      seasons.selected = season
      seasonDialogDelete.title = `${seasons.selected.topic_name} ${seasons.selected.name} を削除しますか？`

      seasonDialogDelete.isOpen = true
    }

    const closeSeasonDialogDelete = () => {
      seasonDialogDelete.isOpen = false
      initializeSelectedSeason()
    }

    const deleteSeason = async () => {
      try {
        const dataObj = {
          season_id: seasons.selected?.id
        }
        const res = await SeasonRepository.deleteSeason(dataObj)

        if (res.status === 200) {
          seasons.list = []
          getSeasons('initial')
          closeSeasonDialogDelete()

          store.dispatch('addFlashMessage', {
            type: 'success',
            message: '大会を削除しました'
          })
        }
      } catch (error) {
        //
      }
    }

    useHead({
      title: computed(() => '大会一覧'),
    })

    const breadcrumbs = computed(() => {
      return [
        {
          type: 'text',
          text: {
            name: '大会一覧'
          }
        },
      ] as Breadcrumb[]
    })

    return {
      seasons,
      seasonsObserverTarget,
      getSeasons,

      topicCategories,
      getTopicCategories,

      searchForm,
      isSearching,

      seasonSubmenuElements,
      openSeasonSubmenu,
      closeSeasonSubmenu,

      seasonDialogArchive,
      openSeasonDialogArchive,
      closeSeasonDialogArchive,
      archiveSeason,

      seasonDialogDelete,
      openSeasonDialogDelete,
      closeSeasonDialogDelete,
      deleteSeason,

      breadcrumbs
    }
  }
})
